import { useEffect, useRef, useState } from "react";
import supabase from '../supabaseClient';
import { getTipoCategoria } from '../constants/formConfig';
import { tieneIncidencia } from '../utils/tieneIncidencia';

const VOLDIS_ALMACEN_ID = "cb60fe34-e079-4e36-8bd9-f5f5c3a1f21c";
const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// "28/8/2026" (toLocaleDateString es-ES) → "2026-08"
const mesClave = (fechaStr) => {
  const [d, m, y] = String(fechaStr || "").split('/');
  if (!d || !m || !y) return null;
  return `${y}-${String(m).padStart(2, '0')}`;
};

const ultimos6Meses = () => {
  const hoy = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - (5 - i), 1);
    return { clave: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${MESES_CORTOS[d.getMonth()]} ${d.getFullYear()}` };
  });
};

// Gauge en semicírculo dibujado directamente en <canvas> vía useEffect —
// un <script> inyectado con dangerouslySetInnerHTML no se ejecuta en el navegador.
const GaugeCanvas = ({ pct, color }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const cx = 45, cy = 46, r = 34, lw = 10;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();
    ctx.arc(cx, cy, r, Math.PI, 2 * Math.PI);
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, r, Math.PI, Math.PI + (pct / 100) * Math.PI);
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.stroke();
  }, [pct, color]);

  return <canvas ref={canvasRef} width="90" height="50" style={{ marginBottom: 4 }} />;
};

// Rosco (círculo completo) — misma técnica que GaugeCanvas, arco de 360º
const DonutCanvas = ({ pct, color, colorFondo = '#E5E7EB' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const cx = 45, cy = 45, r = 34, lw = 10;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, 2 * Math.PI);
    ctx.strokeStyle = colorFondo;
    ctx.lineWidth = lw;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (pct / 100) * 2 * Math.PI);
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.lineCap = 'round';
    ctx.stroke();
  }, [pct, color, colorFondo]);

  return <canvas ref={canvasRef} width="90" height="90" />;
};

// ── ADMIN: KPIs ──────────────────────────────────────────────────
export const KPIsSection = ({ historial, archivo, llegadas, equipos, cargarHistorial, alertasEquipos, alertasDiaria, alertasSemanal, alertasMensual, almacen_id }) => {
  const esVoldis = almacen_id === VOLDIS_ALMACEN_ID;
  const [pedidosVoldis, setPedidosVoldis] = useState([]);

  useEffect(() => {
    if (!esVoldis) return;
    supabase.from('voldis_pedidos').select('cargado, preparado').then(({ data }) => setPedidosVoldis(data || []));
  }, [esVoldis]);

  const totalRegistros = [...historial, ...archivo];
  const pendientes = historial.length;
  const conIncidencia = totalRegistros.filter(r => tieneIncidencia(r)).length;
  const sinIncidencia = totalRegistros.length - conIncidencia;
  const averias = totalRegistros.filter(r => r.tipo === "Avería/Rotura");
  const averiasAbiertas = averias.filter(r => r.estado === "pendiente").length;
  const reservasHoy = totalRegistros.filter(r => r.tipo === "Reserva Traspaleta" && r.datos?.dia === new Date().toISOString().slice(0,10));
  const descargasHoy = llegadas.filter(l => new Date(l.hora_llegada).toDateString()===new Date().toDateString()).length;

  const gauges = [
    { id:"kg1", label:"Con incidencia", val:conIncidencia, sub:"total registros", pct:Math.min(conIncidencia*5,100), color:"#E24B4A" },
    { id:"kg2", label:"Sin incidencia", val:sinIncidencia, sub:"total registros", pct:Math.min(sinIncidencia*5,100), color:"#639922" },
    { id:"kg3", label:"Pendientes", val:pendientes, sub:"sin validar", pct:Math.min(pendientes*10,100), color:"#EF9F27" },
    { id:"kg4", label:"Averías abiertas", val:averiasAbiertas, sub:"sin resolver", pct:Math.min(averiasAbiertas*20,100), color:"#E24B4A" },
    { id:"kg5", label:"Descargas hoy", val:descargasHoy, sub:"operadores", pct:Math.min(descargasHoy*10,100), color:"#378ADD" },
    { id:"kg6", label:"Reservas activas", val:reservasHoy.length, sub:"hoy", pct:Math.min(reservasHoy.length*20,100), color:"#1D9E75" },
  ];

  const areas = ["Almacén","APPCC","Flota","Contenedor SDC","Averías/Roturas"];
  const porArea = areas.map(area => {
    const regs = totalRegistros.filter(r => getTipoCategoria(r.tipo) === area);
    const inc = regs.filter(r => tieneIncidencia(r)).length;
    return { area, total: regs.length, inc, ok: regs.length - inc };
  });

  const porEquipo = (() => {
    const map = {};
    totalRegistros.forEach(r => {
      if (r.tipo === "Avería/Rotura") return;
      const eq = r.datos?.equipo;
      if (!eq) return;
      if (!map[eq]) map[eq] = { total: 0, inc: 0 };
      map[eq].total++;
      if (tieneIncidencia(r)) map[eq].inc++;
    });
    return Object.entries(map)
      .map(([equipo, {total, inc}]) => ({ equipo, total, inc, ok: total - inc }))
      .sort((a, b) => b.inc - a.inc || b.total - a.total)
      .slice(0, 8);
  })();

  const contenedoresPorMes = esVoldis ? (() => {
    const contenedoresSDC = totalRegistros.filter(r => r.tipo === "Contenedor SDC");
    return ultimos6Meses().map(m => ({ ...m, count: contenedoresSDC.filter(r => mesClave(r.fecha) === m.clave).length }));
  })() : [];
  const maxContenedoresMes = Math.max(1, ...contenedoresPorMes.map(m => m.count));

  const pedidosCargados = pedidosVoldis.filter(p => p.cargado).length;
  const pedidosPendientes = pedidosVoldis.length - pedidosCargados;
  const pctPedidosCargados = pedidosVoldis.length > 0 ? Math.round((pedidosCargados / pedidosVoldis.length) * 100) : 0;
  const pedidosPreparados = pedidosVoldis.filter(p => p.preparado).length;
  const pedidosSinPreparar = pedidosVoldis.length - pedidosPreparados;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-gray-800">📊 Panel de KPIs</h2>
        <button onClick={() => cargarHistorial()} className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-lg border border-green-200">🔄 Actualizar</button>
      </div>
      <p className="text-xs text-gray-500">🏭 Equipos activos en inventario: <span className="font-bold text-gray-700">{equipos.filter(eq => eq.Activo).length}</span></p>
      <div className="grid grid-cols-3 gap-3">
        {gauges.map(g => (
          <div key={g.id} className="bg-white rounded-xl border border-gray-200 p-3 flex flex-col items-center">
            <GaugeCanvas pct={g.pct} color={g.color} />
            <p className="text-xs text-gray-500 text-center leading-tight mb-1">{g.label}</p>
            <p className="text-xl font-bold" style={{color:g.color,lineHeight:1}}>{g.val}</p>
            <p className="text-xs text-gray-400 text-center mt-1">{g.sub}</p>
          </div>
        ))}
      </div>

      {esVoldis && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">🌍 Pedidos Internacional</p>
            {pedidosVoldis.length === 0 ? (
              <p className="text-xs text-gray-400">Sin pedidos registrados.</p>
            ) : (
              <div className="flex items-center gap-4">
                <div className="relative flex-shrink-0" style={{ width: 90, height: 90 }}>
                  <DonutCanvas pct={pctPedidosCargados} color="#639922" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-700">{pctPedidosCargados}%</span>
                  </div>
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                    <span className="text-xs text-gray-600 font-semibold">Cargados</span>
                    <span className="text-sm font-bold text-green-600">{pedidosCargados}</span>
                  </div>
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                    <span className="text-xs text-gray-600 font-semibold">Pendientes</span>
                    <span className="text-sm font-bold text-gray-500">{pedidosPendientes}</span>
                  </div>
                </div>
              </div>
            )}
            {pedidosVoldis.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2">
                <div className="text-center">
                  <p className="text-lg font-bold text-blue-600">{pedidosPreparados}</p>
                  <p className="text-[10px] text-gray-500 uppercase font-semibold">Preparados</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-500">{pedidosSinPreparar}</p>
                  <p className="text-[10px] text-gray-500 uppercase font-semibold">Pendientes</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-gray-700">{pedidosVoldis.length}</p>
                  <p className="text-[10px] text-gray-500 uppercase font-semibold">Total</p>
                </div>
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-700 mb-3">📦 Contenedores cargados / mes</p>
            <div className="space-y-1.5">
              {contenedoresPorMes.map(m => (
                <div key={m.clave} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-14 flex-shrink-0">{m.label}</span>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-3 rounded-full bg-blue-500" style={{ width: `${(m.count / maxContenedoresMes) * 100}%` }}></div>
                  </div>
                  <span className="text-xs text-gray-700 font-bold w-5 text-right">{m.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-sm font-semibold text-gray-700 mb-3">🚚 Descargas de hoy</p>
        {descargasHoy === 0
          ? <p className="text-xs text-gray-400">No hay descargas registradas hoy.</p>
          : llegadas.filter(l => new Date(l.hora_llegada).toDateString()===new Date().toDateString()).map(l => (
            <div key={l.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div>
                <p className="text-sm font-semibold text-gray-800">{l.empresa}</p>
                <p className="text-xs text-gray-500">🚛 {l.matricula} · {l.nombre_conductor}</p>
              </div>
              <span className="text-xs text-gray-400">{new Date(l.hora_llegada).toLocaleTimeString("es-ES",{hour:"2-digit",minute:"2-digit"})}</span>
            </div>
          ))
        }
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-sm font-semibold text-gray-700 mb-3">🔔 Alertas — formularios pendientes</p>
        {[...alertasEquipos, ...alertasDiaria, ...alertasSemanal, ...alertasMensual].length === 0
          ? <p className="text-xs text-green-600 font-semibold">✅ Todo al día, sin alertas pendientes.</p>
          : [...alertasEquipos, ...alertasDiaria, ...alertasSemanal, ...alertasMensual].map((a,i) => (
            <div key={i} className={`border rounded-lg px-3 py-2 text-xs font-semibold mb-2 ${a.color}`}>{a.msg}</div>
          ))
        }
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-sm font-semibold text-gray-700 mb-3">📋 Formularios por área</p>
        <div className="space-y-2">
          {porArea.map(({area, total, inc, ok}) => (
            <div key={area} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
              <span className="text-xs text-gray-600 font-semibold">{area}</span>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-gray-400">total <span className="text-gray-700 font-bold">{total}</span></span>
                <span className="text-red-600">⚠ <span className="font-bold">{inc}</span></span>
                <span className="text-green-600">✓ <span className="font-bold">{ok}</span></span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-sm font-semibold text-gray-700 mb-3">🔩 Equipos con más incidencias (Top 8)</p>
        {porEquipo.length === 0 ? (
          <p className="text-xs text-gray-400">Sin registros con equipo asociado.</p>
        ) : (
          <>
            <table className="w-full text-xs mb-3">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left py-1">Equipo</th>
                  <th className="text-right py-1">Total</th>
                  <th className="text-right py-1">⚠</th>
                  <th className="text-right py-1">✓</th>
                </tr>
              </thead>
              <tbody>
                {porEquipo.map(({equipo, total, inc, ok}) => (
                  <tr key={equipo} className="border-b border-gray-50 last:border-0">
                    <td className="py-1 text-gray-700 font-semibold">{equipo}</td>
                    <td className="py-1 text-right text-gray-500">{total}</td>
                    <td className="py-1 text-right text-red-600 font-bold">{inc}</td>
                    <td className="py-1 text-right text-green-600 font-bold">{ok}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="space-y-1.5">
              {porEquipo.map(({equipo, inc, total}) => (
                <div key={equipo} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">{equipo}</span>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-3 rounded-full bg-red-400" style={{width: `${total > 0 ? (inc/total)*100 : 0}%`}}></div>
                  </div>
                  <span className="text-xs text-red-600 font-bold w-6 text-right">{inc}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">📅 Reservas activas hoy</p>
          {reservasHoy.length === 0
            ? <p className="text-xs text-gray-400">Sin reservas hoy.</p>
            : reservasHoy.map(r => (
              <div key={r.id} className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-0">
                <span className="text-xs font-bold px-2 py-0.5 rounded" style={{background:"#E6F1FB",color:"#0C447C"}}>{r.datos?.equipo}</span>
                <span className="text-xs text-gray-700 flex-1">{r.datos?.nombre}</span>
                <span className="text-xs text-gray-400">{r.datos?.horaRecogida}–{r.datos?.horaDevolucion}</span>
              </div>
            ))
          }
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">🔧 Averías / Roturas</p>
          {averias.length === 0
            ? <p className="text-xs text-gray-400">Sin averías registradas.</p>
            : averias.slice(0,4).map(r => (
              <div key={r.id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                <div>
                  <p className="text-xs font-semibold text-gray-800">{r.datos?.equipo}</p>
                  <p className="text-xs text-gray-500 truncate" style={{maxWidth:80}}>{r.datos?.incidencia}</p>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.estado==="validado"?"bg-green-100 text-green-800":"bg-yellow-100 text-yellow-800"}`}>
                  {r.estado==="validado"?"Resuelta":"Pendiente"}
                </span>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};
